import { useRef } from 'react';

import { storage } from '@utils/browser';

type IntercomUser = {
  id: number | string;
  email: string;
  name: string;
  role?: string;
  language?: string;
  picture?: string | null;
  companyId: number;
  companyName: string;
};

const regionURLs = {
  us: 'https://api-iam.intercom.io',
  eu: 'https://api-iam.eu.intercom.io',
} as const;

const debugToken = storage.get('dbg');

// @ts-ignore
// prettier-ignore
function initialize(appID: string) { if (typeof window.Intercom === 'function') { window.Intercom('reattach_activator'); window.Intercom('update', window.intercomSettings); } else { let d = document; let i = function () { i.c(arguments); }; i.q = []; i.c = function (args) { i.q.push(args); }; window.Intercom = i; let l = function () { let s = d.createElement('script'); s.type = 'text/javascript'; s.async = true; s.src = 'https://widget.intercom.io/widget/' + appID; let x = d.getElementsByTagName('script')[0]; x.parentNode.insertBefore(s, x); }; if (document.readyState === 'complete') { l(); } else if (window.attachEvent) { window.attachEvent('onload', l); } else { window.addEventListener('load', l, false); } } }

const createIntercomStore = () => {
  const system = process.env.VITE_APP;
  const region = (process.env.VITE_INTERCOM_REGION ?? 'us') as keyof typeof regionURLs;
  const shouldShowBubble = process.env.VITE_INTERCOM_BUBBLE === 'true';
  const appId = process.env.VITE_INTERCOM_APPID;
  const isEnabled = Boolean(!debugToken && appId);

  let isStarted = false;

  const showMessenger = () => {
    if (!isEnabled) return;
    window.Intercom('show');
  };

  const setUserData = (user: IntercomUser) => {
    if (!isEnabled) return;

    const data = {
      user_id: user.id,
      email: user.email,
      name: user.name,
      '50skills_role': user.role,
      '50skills_system': system,
      language_override: user.language ?? 'en',
      avatar: {
        type: 'avatar',
        image_url: user.picture,
      },
      company: {
        company_id: user.companyId,
        name: user.companyName,
      },
      buildDate: window.buildDate,
    };

    if (!isStarted) {
      initialize(appId as string);

      window.Intercom('boot', {
        app_id: appId,
        hide_default_launcher: !shouldShowBubble,
        ...data,
      });

      isStarted = true;
    } else {
      window.Intercom('update', data);
    }

    window.intercomSettings = {
      app_id: appId,
      api_base: regionURLs[region],
      ...data,
    };
  };

  return {
    isEnabled,
    showMessenger,
    setUserData,
  };
};

const intercomStore = createIntercomStore();

export const useIntercom = () => {
  // Make sure it's just a singleton being used, and this way anyone can call useIntercom()
  // but it only creates the one store and wont set up multiple event handlers etc.
  return useRef(intercomStore).current;
};

declare global {
  interface Window {
    buildDate: string;
    Intercom?: any;
    intercomSettings?: any;
  }
}
